import tokenManager from "@/tokenManager";

const AUTH_TERMINATION_CODE = "AUTH_SESSION_TERMINATED";
let terminationStarted = false;

const safeCommit = (store, mutation, payload) => {
  if (!store) return;

  try {
    store.commit(mutation, payload);
  } catch (_) {
    // Session cleanup must continue even if a store module is unavailable.
  }
};

const clearApplicationCaches = () => {
  if (typeof window === "undefined" || !("caches" in window)) return;

  window.caches
    .keys()
    .then((cacheNames) => Promise.all(cacheNames.map((name) => window.caches.delete(name))))
    .catch(() => {});
};

export const markAuthenticationTermination = (error) => {
  if (error && typeof error === "object") {
    error.code = AUTH_TERMINATION_CODE;
    error.isAuthenticationTermination = true;
  }

  return error;
};

export const isAuthenticationTermination = (error) =>
  error?.code === AUTH_TERMINATION_CODE || error?.isAuthenticationTermination === true;

let inactivityTimer = null;
let appStore = null;
const DEFAULT_INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;

const hasActiveSession = (store) => {
  if (typeof window === "undefined") {
    return false;
  }

  const hasStoreLoginFlag = Boolean(
    store?.getters?.isLoggedIn
    || store?.state?.authorization?.isLoggedIn
  );
  const hasStoredUser = Boolean(window.localStorage.getItem("user"));
  const hasStoredToken = Boolean(
    window.localStorage.getItem("access_token")
    || tokenManager.getToken()
  );

  return hasStoreLoginFlag || hasStoredUser || hasStoredToken || tokenManager.isUsingHttpOnlyCookies();
};

export const setSessionStore = (storeInstance) => {
  appStore = storeInstance;
};

export const clearInactivityTimer = () => {
  if (inactivityTimer) {
    console.log("[sessionManager] clearInactivityTimer", { inactivityTimer: inactivityTimer._idleTimeout, timeoutMs: DEFAULT_INACTIVITY_TIMEOUT_MS });
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
};

export const startInactivityTimer = ({
  store,
  timeoutMs = DEFAULT_INACTIVITY_TIMEOUT_MS,
  onTimeout = null,
} = {}) => {
  clearInactivityTimer();

  console.log("[sessionManager] startInactivityTimer", {
    timeoutMs,
    hasStore: Boolean(store && typeof store.dispatch === "function"),
    hasActiveSession: hasActiveSession(store || appStore),
  });

  inactivityTimer = setTimeout(() => {
    console.log("[sessionManager] inactivity timeout fired", {
      timeoutMs,
      timestamp: new Date().toISOString(),
    });

    if (typeof onTimeout === "function") {
      onTimeout();
      return;
    }

    if (store && typeof store.dispatch === "function") {
      console.log("[sessionManager] dispatching logout");
      store.dispatch("logout");
    }
  }, timeoutMs);

  return inactivityTimer;
};

export const resetInactivityTimer = ({
  store,
  timeoutMs = DEFAULT_INACTIVITY_TIMEOUT_MS,
  onTimeout = null,
} = {}) => {
  if (typeof window === "undefined") {
    return null;
  }

  const activeStore = store || appStore;
  const isLoggedIn = hasActiveSession(activeStore);

  console.log("[sessionManager] resetInactivityTimer", {
    reason: "route_or_authenticated_request",
    timeoutMs,
    isLoggedIn,
    hasStore: Boolean(activeStore),
    timestamp: new Date().toISOString(),
  });

  if (!isLoggedIn) {
    clearInactivityTimer();
    return null;
  }

  return startInactivityTimer({ store: activeStore, timeoutMs, onTimeout });
};

export const bindInactivityTracking = ({
  store,
  timeoutMs = DEFAULT_INACTIVITY_TIMEOUT_MS,
  onTimeout = null,
} = {}) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  setSessionStore(store || appStore);
  resetInactivityTimer({ store: store || appStore, timeoutMs, onTimeout });

  return () => {
    clearInactivityTimer();
  };
};

export const resetTerminationState = () => {
  terminationStarted = false;
};

export const clearClientSession = ({ store, preserveKeys = ["darkMode", "language"] } = {}) => {
  const preservedValues = {};

  try {
    preserveKeys.forEach((key) => {
      const value = window.localStorage.getItem(key);
      if (value !== null) preservedValues[key] = value;
    });
  } catch (_) {
    // Storage can be unavailable in restricted/private browser contexts.
  }

  try {
    tokenManager.clearToken();
  } catch (_) {
    // Continue clearing the remaining session data.
  }

  try {
    const keysToRemove = [];
    for (const key of Object.keys(window.localStorage)) {
      if (!preserveKeys.includes(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => window.localStorage.removeItem(key));

    Object.entries(preservedValues).forEach(([key, value]) => {
      window.localStorage.setItem(key, value);
    });
  } catch (_) {
    // Continue with session and in-memory cleanup.
  }

  try {
    window.sessionStorage.clear();
  } catch (_) {
    // Continue with in-memory cleanup.
  }

  safeCommit(store, "setAccessToken", "");
  safeCommit(store, "setLoggedUser", {});
  safeCommit(store, "setIsLoggedIn", false);
  safeCommit(store, "clearAuthorization");
  safeCommit(store, "setRolePermissions", []);
  safeCommit(store, "setPermissions", []);
  safeCommit(store, "setRoles", []);
  safeCommit(store, "setRole", {});
  safeCommit(store, "setSelectedRole", {});
  safeCommit(store, "setToast", {
    display: false,
    type: null,
    title: null,
    message: null,
  });

  clearApplicationCaches();
};

export const forceLoginRedirect = ({ store } = {}) => {
  if (terminationStarted) return false;

  terminationStarted = true;
  clearInactivityTimer();
  clearClientSession({ store, preserveKeys: ["darkMode", "language"] });

  if (typeof window !== "undefined") {
    const loginUrl = `${process.env.BASE_URL || "/"}login`.replace(/\/+/g, "/");
    window.location.replace(loginUrl);
  }

  return true;
};

export const hasSessionTerminationStarted = () => terminationStarted;
